import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNotify, useRefresh, useRecordContext } from 'react-admin';
import { tenantService } from '../services/tenantService';

interface EquipmentPrimaryPhotoProps {
  equipmentId?: number;
  readOnly?: boolean;
}

export const EquipmentPrimaryPhoto: React.FC<EquipmentPrimaryPhotoProps> = ({ 
  equipmentId: propEquipmentId, 
  readOnly = false 
}) => {
  const record = useRecordContext();
  const equipmentId = propEquipmentId || record?.id;
  const notify = useNotify();
  const refresh = useRefresh();
  const [primaryPhoto, setPrimaryPhoto] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Fetch equipment to get primary photo
  const fetchPrimaryPhoto = async () => {
    if (!equipmentId) return;
    
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();
      
      const response = await fetch(`${baseUrl}/equipment/${equipmentId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrimaryPhoto(data.primaryPhoto);
      }
    } catch (error) {
      console.error('Error fetching primary photo:', error);
    }
  };

  useEffect(() => {
    fetchPrimaryPhoto();
  }, [equipmentId]);


  // Handle photo upload
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      notify('Please select an image file', { type: 'error' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notify('Image size must be less than 10MB', { type: 'error' });
      return;
    }

    setUploading(true);
    
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      // Use the new combined endpoint that uploads and sets primary photo in one call
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${baseUrl}/equipment/${equipmentId}/upload-primary-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        notify('Primary photo updated successfully', { type: 'success' });
        // Update the state directly with the response if it includes the photo
        if (result.primaryPhoto) {
          setPrimaryPhoto(result.primaryPhoto);
        }
        fetchPrimaryPhoto();
        refresh();
      } else {
        const errorText = await response.text();
        throw new Error('Failed to upload primary photo');
      }
    } catch (error: any) {
      notify(`Failed to upload photo: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // Handle photo removal
  const handlePhotoRemove = async () => {
    if (!confirm('Are you sure you want to remove the primary photo?')) {
      return;
    }

    setLoading(true);
    
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const response = await fetch(`${baseUrl}/equipment/${equipmentId}/primary-photo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });

      if (response.ok) {
        notify('Primary photo removed successfully', { type: 'success' });
        setPrimaryPhoto(null);
        refresh();
      } else {
        throw new Error('Failed to remove primary photo');
      }
    } catch (error: any) {
      notify(`Failed to remove photo: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!equipmentId) {
    return (
      <Paper elevation={1} sx={{ p: 3 }}>
        <Alert severity="info">Save the equipment first to add a primary photo</Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
        Primary Photo
      </Typography>

      <Box sx={{ maxWidth: 400, mx: 'auto' }}>
        {primaryPhoto ? (
          <Card>
            <CardMedia
              component="img"
              height="300"
              image={primaryPhoto.url}
              alt="Equipment primary photo"
              sx={{ objectFit: 'cover' }}
            />
            {!readOnly && (
              <CardContent sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="replace-photo-input"
                  type="file"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
                <label htmlFor="replace-photo-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<EditIcon />}
                    disabled={uploading}
                  >
                    Replace
                  </Button>
                </label>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handlePhotoRemove}
                  disabled={loading}
                >
                  Remove
                </Button>
              </CardContent>
            )}
          </Card>
        ) : (
          <Card sx={{ 
            height: 300, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '2px dashed #e0e0e0',
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CameraIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No primary photo
              </Typography>
              {!readOnly && (
                <>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="upload-photo-input"
                    type="file"
                    onChange={handlePhotoUpload}
                    disabled={uploading}
                  />
                  <label htmlFor="upload-photo-input">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                      disabled={uploading}
                      sx={{ mt: 2 }}
                    >
                      {uploading ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  </label>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 2 }} color="text.secondary">
        Recommended: Square image, at least 800x800 pixels
      </Typography>
    </Paper>
  );
};